import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook that tracks whether an element is visible in the viewport
 * @param {Object} options - Intersection Observer options
 * @param {number} options.threshold - Minimum percentage of element that must be visible (0-1)
 * @param {string} options.rootMargin - Margin around the root element
 * @param {Element} options.root - Root element for intersection (defaults to viewport)
 * @returns {Array} [ref, isVisible] - Ref to attach to element and visibility state
 */
const useIsVisible = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef();

  const { threshold = 0.1, rootMargin = '0px', root = null } = options;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold,
        rootMargin,
        root,
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin, root]);

  return [ref, isVisible];
};

export default useIsVisible;
